import { Box, BoxProps, ButtonProps, IconButton, IconButtonProps } from '@chakra-ui/react';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { ReactElement, ReactNode, createContext, useContext, useEffect, useRef } from 'react';
import { Icon } from '../Icon/Icon';
import { carouselCss, iconProps, slideCarouselCss } from './Carousel.styles';
import { CarouselType, Direction, UseCarouselProps, UseCarouselResponse } from './types';
import { useCarousel } from './useCarousel';
import { useSlideCarousel } from './useSlideCarousel';

const carouselTypes: Record<
  CarouselType,
  {
    hook: (props: UseCarouselProps) => UseCarouselResponse;
    styles: BoxProps['css'];
  }
> = {
  fade: {
    hook: useCarousel,
    styles: carouselCss,
  },
  slide: {
    hook: useSlideCarousel,
    styles: slideCarouselCss,
  },
};

const CarouselContext = createContext<UseCarouselResponse | undefined>(undefined);
CarouselContext.displayName = 'CarouselContext';
const useCarouselContext = (): UseCarouselResponse => {
  const context = useContext(CarouselContext);
  if (!context) throw new Error('Missing carousel context');
  return context;
};

export interface CarouselProps extends BoxProps, UseCarouselProps {
  type?: CarouselType;
}

const Carousel = ({
  type = 'slide',
  loop,
  interval,
  allowDrag,
  ...rest
}: CarouselProps): ReactElement | null => {
  const { hook, styles } = useRef(carouselTypes[type]).current;
  const value = hook({ loop, interval });
  return (
    <CarouselContext.Provider value={value}>
      <Box
        position="relative"
        css={styles}
        {...rest}
        {...(allowDrag && value.drag
          ? { ...value.drag(), 'touch-action': 'none', userSelect: 'none' }
          : {})}
      />
    </CarouselContext.Provider>
  );
};

const Items = ({
  children,
  ...rest
}: BoxProps & { children: ReactNode[] }): ReactElement | null => {
  const { props, setLength } = useCarouselContext();
  const len = children.length;
  useEffect(() => {
    setLength(len);
  }, [setLength, len]);

  return (
    <Box {...rest} {...props} className="items">
      {children}
    </Box>
  );
};

const RightArrow = ({
  children,
  showDisabled = true,
  ...rest
}: Partial<IconButtonProps> & {
  showDisabled?: boolean;
}): ReactElement | null => {
  const { arrows, slide } = useCarouselContext();
  if (!arrows.has) return null;
  if (!showDisabled && !arrows.right) return null;
  return (
    <Box
      className={arrows.right ? 'enabled' : 'disabled'}
      data-arrow="right"
      onClick={arrows.right ? () => slide(Direction.Right) : undefined}
    >
      {children || (
        <IconButton {...iconProps} aria-label="Next" isDisabled={!arrows.right} {...rest}>
          <Icon icon={faChevronRight} />
        </IconButton>
      )}
    </Box>
  );
};
const LeftArrow = ({
  children,
  showDisabled = true,
  ...rest
}: Partial<IconButtonProps> & {
  showDisabled?: boolean;
}): ReactElement | null => {
  const { arrows, slide } = useCarouselContext();
  if (!arrows.has) return null;
  if (!showDisabled && !arrows.left) return null;
  return (
    <Box
      className={arrows.left ? 'enabled' : 'disabled'}
      data-arrow="left"
      onClick={arrows.left ? () => slide(Direction.Left) : undefined}
    >
      {children || (
        <IconButton {...iconProps} aria-label="Previous" isDisabled={!arrows.left} {...rest}>
          <Icon icon={faChevronLeft} />
        </IconButton>
      )}
    </Box>
  );
};

const Navigation = ({ dark, ...rest }: ButtonProps & { dark?: boolean }): ReactElement | null => {
  const { index, length, setSlide } = useCarouselContext();
  const len = Math.max(length, 0);
  const colors = dark ? ['white', 'whiteAlpha.400'] : ['blackAlpha.400', 'blackAlpha.200'];
  return (
    <>
      {Array(len)
        .fill(null)
        .map((_, i) => (
          <IconButton
            variant="simple"
            key={i}
            w={4}
            h={4}
            px={0}
            mr={4}
            rounded="full"
            minW="none"
            aria-label="Navigation"
            bgColor={index === i ? colors[0] : colors[1]}
            onClick={() => setSlide(i, true)}
            _hover={{ opacity: 0.9 }}
            {...rest}
          />
        ))}
    </>
  );
};

const Number = (): ReactElement | null => {
  const { index, length } = useCarouselContext();
  return (
    <>
      {index + 1}/{length}
    </>
  );
};

const Context = ({
  children,
}: {
  children: (ctx: UseCarouselResponse) => ReactNode;
}): ReactElement | null => {
  const c = useCarouselContext();
  return <>{children(c)}</>;
};

Carousel.RightArrow = RightArrow;
Carousel.LeftArrow = LeftArrow;
Carousel.Navigation = Navigation;
Carousel.Number = Number;
Carousel.Items = Items;
Carousel.Context = Context;
export default Carousel;
